import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "Download as CSV" button — same plumbing as DownloadMdButton but
 * takes an array of row-objects (built by the caller) and writes a real CSV.
 *
 * Usage:
 *   <DownloadCsvButton filenamePrefix="inphase-audit-issues" buildRows={() => rows} />
 */
function csvEscape(val) {
    const s = val === null || val === undefined ? "" : String(val);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

export function DownloadCsvButton({
    filenamePrefix,
    buildRows,
    disabled = false,
    testId = "download-csv-btn",
    label = "Export CSV",
    className = "",
}) {
    const [busy, setBusy] = useState(false);

    const onClick = async () => {
        setBusy(true);
        try {
            const rows = await Promise.resolve(buildRows());
            if (!rows || rows.length === 0) {
                toast.warning("Nothing to export yet — load some data first.");
                return;
            }
            const headers = Object.keys(rows[0]);
            const lines = [
                headers.join(","),
                ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
            ];
            const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${filenamePrefix}-${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${a.download}`);
        } catch (err) {
            toast.error(err?.message || "Couldn't build CSV export");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy || disabled}
            data-testid={testId}
            title="Download this data as a CSV file"
            className={`border border-border px-3 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 ${className}`}
        >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            {label}
        </button>
    );
}
